import { useState, useEffect } from 'react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Label } from '@/components/ui/label'
import { useToast } from '@/hooks/use-toast'
import { useCreateLead, useUsers } from '@/hooks/use-api'
import { usePicklistOptions } from '@/hooks/use-picklists'
import { DuplicateWarning } from '@/components/DuplicateWarning'
import { useUser } from '@/contexts/UserContext'
import { ResponsiveFormGrid, MobileScrollableForm } from '@/components/ui/responsive-form'
import type { Lead } from '@/types'
import { explainError } from '@/lib/explain-error'

interface CreateLeadModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onCreated?: (lead: Lead) => void
}

export function CreateLeadModal({ open, onOpenChange, onCreated }: CreateLeadModalProps) {
  const { toast } = useToast()
  const { currentUser } = useUser()
  const createMutation = useCreateLead()
  const { data: usersData } = useUsers({ limit: 10000 }) // Load all users for dropdown

  const leadStatuses = usePicklistOptions('lead', 'status')
  const leadRatings = usePicklistOptions('lead', 'rating')
  const leadIndustries = usePicklistOptions('lead', 'industry')
  const leadSources = usePicklistOptions('lead', 'lead_source')

  const [formData, setFormData] = useState<Partial<Lead>>({
    first_name: '',
    last_name: '',
    company: '',
    email: '',
    phone: '',
    mobile_phone: '',
    title: '',
    status: 'New',
    industry: '',
    rating: '',
    owner_id: currentUser?.id || '',
    lead_source: '',
    lead_source_details__c: '',
    description: '',
  })

  // Update owner_id when currentUser changes or modal opens
  useEffect(() => {
    if (open && currentUser?.id) {
      setFormData((prev) => ({ ...prev, owner_id: currentUser.id }))
    }
  }, [open, currentUser?.id])

  const [errors, setErrors] = useState<Record<string, string>>({})

  const users = usersData?.data || []

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {}

    if (!formData.last_name?.trim()) {
      newErrors.last_name = 'Last name is required'
    }

    // Email is required and must be valid
    if (!formData.email?.trim()) {
      newErrors.email = 'Email is required'
    } else {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
      if (!emailRegex.test(formData.email)) {
        newErrors.email = 'Please enter a valid email address'
      }
    }

    // Lead source is required
    if (!formData.lead_source?.trim()) {
      newErrors.lead_source = 'Lead source is required'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!validate()) {
      toast({
        title: 'Validation Error',
        description: 'Please fill in all required fields correctly.',
        variant: 'destructive',
      })
      return
    }

    try {
      const result = await createMutation.mutateAsync(formData)

      toast({
        title: 'Success',
        description: 'Lead created successfully.',
      })

      if (onCreated && result?.id) {
        onCreated(result as Lead)
      }

      // Reset form and close modal
      setFormData({
        first_name: '',
        last_name: '',
        company: '',
        email: '',
        phone: '',
        mobile_phone: '',
        title: '',
        status: 'New',
        industry: '',
        rating: '',
        owner_id: currentUser?.id || '',
        lead_source: '',
        lead_source_details__c: '',
        description: '',
      })
      setErrors({})
      onOpenChange(false)
    } catch (error) {
      const errorMessage = explainError(error, 'Failed to create lead')
      toast({
        title: 'Error',
        description: errorMessage,
        variant: 'destructive',
      })
    }
  }

  const handleChange = (field: keyof typeof formData, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }))
    // Clear error for this field
    if (errors[field]) {
      setErrors((prev) => {
        const newErrors = { ...prev }
        delete newErrors[field]
        return newErrors
      })
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-[calc(100vw-2rem)] sm:max-w-2xl max-h-[calc(100vh-4rem)] sm:max-h-[90vh] overflow-y-auto"
        aria-describedby="create-lead-description"
      >
        <DialogHeader>
          <DialogTitle>Create New Lead</DialogTitle>
          <DialogDescription id="create-lead-description">
            Fill in the details to create a new lead. Fields marked with * are required.
          </DialogDescription>
        </DialogHeader>

        <MobileScrollableForm>
          <form onSubmit={handleSubmit} className="space-y-4">
            <ResponsiveFormGrid>
              {/* First Name */}
              <div>
                <Label htmlFor="first_name">First Name</Label>
                <Input
                  id="first_name"
                  value={formData.first_name}
                  onChange={(e) => handleChange('first_name', e.target.value)}
                  placeholder="Enter first name"
                />
              </div>

              {/* Last Name */}
              <div>
                <Label htmlFor="last_name" className="required">
                  Last Name *
                </Label>
                <Input
                  id="last_name"
                  value={formData.last_name}
                  onChange={(e) => handleChange('last_name', e.target.value)}
                  placeholder="Enter last name"
                  aria-required="true"
                  aria-invalid={!!errors.last_name}
                  aria-describedby={errors.last_name ? 'last_name-error' : undefined}
                />
                {errors.last_name && (
                  <p id="last_name-error" className="text-sm text-destructive mt-1" role="alert">
                    {errors.last_name}
                  </p>
                )}
              </div>
            </ResponsiveFormGrid>

            {/* Company */}
            <div>
              <Label htmlFor="company">Company</Label>
              <Input
                id="company"
                value={formData.company}
                onChange={(e) => handleChange('company', e.target.value)}
                placeholder="Enter company name"
              />
            </div>

            {/* Title */}
            <div>
              <Label htmlFor="title">Title</Label>
              <Input
                id="title"
                value={formData.title}
                onChange={(e) => handleChange('title', e.target.value)}
                placeholder="Enter job title"
              />
            </div>

            <ResponsiveFormGrid>
              {/* Email */}
              <div>
                <Label htmlFor="email" className="required">
                  Email *
                </Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleChange('email', e.target.value)}
                  placeholder="Enter email address"
                  aria-required="true"
                  aria-invalid={!!errors.email}
                  aria-describedby={errors.email ? 'email-error' : undefined}
                />
                {errors.email && (
                  <p id="email-error" className="text-sm text-destructive mt-1" role="alert">
                    {errors.email}
                  </p>
                )}
              </div>

              {/* Phone */}
              <div>
                <Label htmlFor="phone">Phone</Label>
                <Input
                  id="phone"
                  value={formData.phone}
                  onChange={(e) => handleChange('phone', e.target.value)}
                  placeholder="Enter phone number"
                />
              </div>
            </ResponsiveFormGrid>

            {/* Mobile Phone */}
            <div>
              <Label htmlFor="mobile_phone">Mobile Phone</Label>
              <Input
                id="mobile_phone"
                value={formData.mobile_phone}
                onChange={(e) => handleChange('mobile_phone', e.target.value)}
                placeholder="Enter mobile phone number"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              {/* Status */}
              <div>
                <Label htmlFor="status">Status</Label>
                <select
                  id="status"
                  value={formData.status}
                  onChange={(e) => handleChange('status', e.target.value)}
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                >
                  {leadStatuses.map((status) => (
                    <option key={status} value={status}>
                      {status}
                    </option>
                  ))}
                </select>
              </div>

              {/* Rating */}
              <div>
                <Label htmlFor="rating">Rating</Label>
                <select
                  id="rating"
                  value={formData.rating}
                  onChange={(e) => handleChange('rating', e.target.value)}
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                >
                  <option value="">Select rating...</option>
                  {leadRatings.map((rating) => (
                    <option key={rating} value={rating}>
                      {rating}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              {/* Industry */}
              <div>
                <Label htmlFor="industry">Industry</Label>
                <select
                  id="industry"
                  value={formData.industry}
                  onChange={(e) => handleChange('industry', e.target.value)}
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                >
                  <option value="">Select industry...</option>
                  {leadIndustries.map((industry) => (
                    <option key={industry} value={industry}>
                      {industry}
                    </option>
                  ))}
                </select>
              </div>

              {/* Lead Source */}
              <div>
                <Label htmlFor="lead_source" className="required">
                  Lead Source *
                </Label>
                <select
                  id="lead_source"
                  value={formData.lead_source}
                  onChange={(e) => handleChange('lead_source', e.target.value)}
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                  aria-required="true"
                  aria-invalid={!!errors.lead_source}
                  aria-describedby={errors.lead_source ? 'lead_source-error' : undefined}
                >
                  <option value="">Select lead source...</option>
                  {leadSources.map((source) => (
                    <option key={source} value={source}>
                      {source}
                    </option>
                  ))}
                </select>
                {errors.lead_source && (
                  <p id="lead_source-error" className="text-sm text-destructive mt-1" role="alert">
                    {errors.lead_source}
                  </p>
                )}
              </div>
            </div>

            {/* Lead Source Details */}
            <div>
              <Label htmlFor="lead_source_details__c">Lead Source Details</Label>
              <Input
                id="lead_source_details__c"
                value={formData.lead_source_details__c || ''}
                onChange={(e) => handleChange('lead_source_details__c', e.target.value)}
                placeholder="Enter additional details about the lead source"
              />
            </div>

            {/* Description / notes */}
            <div>
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                value={formData.description || ''}
                onChange={(e) => handleChange('description', e.target.value)}
                placeholder="Notes about this lead..."
                rows={3}
              />
            </div>

            {/* Owner */}
            <div>
              <Label htmlFor="owner_id">Owner</Label>
              <select
                id="owner_id"
                value={formData.owner_id}
                onChange={(e) => handleChange('owner_id', e.target.value)}
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
              >
                <option value="">Select owner...</option>
                {[...users]
                  .filter((u) => ['User', 'Admin', 'Super Admin'].includes(u.user_type))
                  .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
                  .map((user) => (
                    <option key={user.id} value={user.id}>
                      {user.name}
                    </option>
                  ))}
              </select>
            </div>

            {/* Duplicate Warning */}
            <DuplicateWarning entityType="Lead" formData={formData as Record<string, unknown>} />

            {/* Form Actions */}
            <div className="flex justify-end gap-3 pt-4 border-t">
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={createMutation.isPending}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={createMutation.isPending}>
                {createMutation.isPending ? 'Creating...' : 'Create Lead'}
              </Button>
            </div>
          </form>
        </MobileScrollableForm>
      </DialogContent>
    </Dialog>
  )
}
